import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const PLANS = [
    { id: 'morgan_price_core_ip',  key: 'core_ip',  label: 'Core',      modules: ['core_ip'] },
    { id: 'morgan_price_core_op1', key: 'core_op1', label: 'Core + OP1', modules: ['core_ip', 'op_module_1'] },
    { id: 'morgan_price_core_op2', key: 'core_op2', label: 'Core + OP2', modules: ['core_ip', 'op_module_2'] },
];

const BASE_MODULES = [
    { key: 'core_ip',    label: 'Core IP',    premiumField: 'core_ip_premium' },
    { key: 'op_module_1', label: 'OP Module 1', premiumField: 'op_module_1_premium' },
    { key: 'op_module_2', label: 'OP Module 2', premiumField: 'op_module_2_premium' },
];

const OPTIONAL_COVERS = [
    { key: 'remove_op_co_insurance',      label: 'OP Co-Insurance',        premiumField: 'remove_op_co_insurance_premium' },
    { key: 'reduce_overall_annual_limit', label: 'Reduce SI',               premiumField: 'reduce_overall_annual_limit_premium' },
    { key: 'dental',                      label: 'Dental',                  premiumField: 'dental_premium' },
    { key: 'maternity_module',            label: 'Maternity Module',        premiumField: 'maternity_module_premium' },
    { key: 'enhanced_network_module',     label: 'Enhanced Network Module', premiumField: 'enhanced_network_module_premium' },
    { key: 'increase_si',                 label: 'Increase SI',             premiumField: 'increase_si_premium' },
];

const ALL_MODULES = [...BASE_MODULES, ...OPTIONAL_COVERS];

// Morgan Price is USD only (Quotation1 defaults it to USD and MorganPrice_Rates hides the currency select)
const DEFAULT_CURRENCY = 'USD';
const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#0f7ac0';

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as MorganPrice_Rates: discount/loading scale every module rate
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function MorganPrice_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.morgan_price_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Worldwide excluding USA') ? 'Worldwide excluding USA' : (areaOptions[0] || '');
    // { [short name]: area of coverage } — the select shows the short name, like MorganPrice_Rates
    const areaLabels = appConfig?.content?.specifics?.morgan_price?.area_of_coverages || {};
    const excessOptions = Object.keys(appConfig?.content?.specifics?.morgan_price?.excess_in_patient_treatment || {});

    const [selectedPlan, setSelectedPlan] = useState('morgan_price_core_ip');
    // Set of selected optional cover keys
    const [selectedOptionalCovers, setSelectedOptionalCovers] = useState(new Set());
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: string } — excess/deductible per quote
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
    const activeBaseModules = BASE_MODULES.filter(m => selectedPlanObj.modules.includes(m.key));
    const activeOptionalCovers = OPTIONAL_COVERS.filter(c => selectedOptionalCovers.has(c.key));
    const allActiveModules = [...activeBaseModules, ...activeOptionalCovers];

    function getAreaLabel(area) {
        return Object.keys(areaLabels).find(key => areaLabels[key] === area) || area;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            setSelectedPlan(editingPlan.base_plan_id || editingPlan.plan_id);
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || DEFAULT_CURRENCY);
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setSelectedOptionalCovers(new Set(editingPlan.payloads?.optional_covers || []));
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored per-module premiums
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return Object.fromEntries(ALL_MODULES.map(m => [m.key, unscale(premium?.[m.premiumField] ?? null)]));
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open) {
            setSelectedPlan('morgan_price_core_ip');
            setSelectedOptionalCovers(new Set());
            setAreaOfCoverage(defaultArea);
            setCurrency(DEFAULT_CURRENCY);
            setPaymentFrequency('Annually');
            setDiscount('0');
            setLoadingPercent('0');
            const defaults = {};
            (quotes || []).forEach(q => {
                defaults[q.name] = excessOptions[0] || '';
            });
            setQuoteDeductibles(defaults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Every module and optional cover rate comes back in one fetch, so toggling a cover just
    // shows a different set of rows — no refresh needed.
    function toggleOptionalCover(key) {
        setSelectedOptionalCovers(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select an excess for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { morgan_price: '' },
                            insurers: ['morgan_price'],
                            specifics: {
                                morgan_price: {
                                    excess_in_patient_treatment: quoteDeductibles[quote.name] || '',
                                },
                            },
                            specific_area_of_coverage: { morgan_price: areaOfCoverage },
                            rates_version_per_insurer: { morgan_price: selectedRatesVersion?.morgan_price || '' },
                            specific_currency: { morgan_price: currency },
                            specific_payment_frequency: { morgan_price: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.morgan_price }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(ALL_MODULES.map(m => [m.key, r[m.key]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Morgan Price rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Individual module rate with discount/loading applied; ratesData keeps the raw API rates
    function getRawRate(clientIndex, moduleKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        const rate = ratesData[quoteUid].rawRates[clientIndex]?.[moduleKey];
        if (rate === undefined || rate === null) return null;
        return typeof rate === 'number' ? rate * rateFactor : rate;
    }

    // Total rate for a client = sum of all active modules (base + optional)
    function getRate(clientIndex, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates?.[clientIndex]) return null;
        let total = 0;
        let hasAny = false;
        for (const m of allActiveModules) {
            const r = getRawRate(clientIndex, m.key, quoteUid);
            if (typeof r === 'number') { total += r; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function sumClients(getter) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getter(i);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildDeductibleString(quoteName) {
        const val = quoteDeductibles[quoteName] || '';
        if (!val) return '';
        const isZero = val.toLowerCase().includes('zero') || val.toLowerCase().includes('nil');
        return isZero ? 'Zero Excess' : `${val} Excess`;
    }

    function buildPlanLabel() {
        return activeOptionalCovers.length > 0
            ? `${selectedPlanObj.label} + ${activeOptionalCovers.map(c => c.label).join(' + ')}`
            : selectedPlanObj.label;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const activeModuleKeys = allActiveModules.map(m => m.key);
        const coversSuffix = activeOptionalCovers.map(c => c.key).join('_');
        const effectivePlanId = editingPlan
            ? editingPlan.plan_id
            : (coversSuffix ? `${selectedPlan}_${coversSuffix}` : selectedPlan);
        // plan_id identifies the tier + covers combination and is shared by every instance of it;
        // uid identifies this specific plan instance so multiple plans of the same combination
        // can coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getRate(clientIndex, quote.uid);
                // Only persist the premium for modules in this plan — the API returns a rate for
                // every module regardless of selection. Stored with discount/loading applied.
                const modulePremiums = Object.fromEntries(ALL_MODULES.map(m => [
                    m.premiumField,
                    activeModuleKeys.includes(m.key) ? getRawRate(clientIndex, m.key, quote.uid) : null,
                ]));
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: effectivePlanId,
                    planUid,
                    premium: {
                        insurer: 'morgan_price',
                        plan: selectedPlanObj.key,
                        plan_label: planLabel,
                        included_benefits: '',
                        deductible: buildDeductibleString(quote.name),
                        area_of_coverage: areaOfCoverage,
                        currency,
                        payment_frequency: paymentFrequency,
                        optional_covers: activeOptionalCovers.map(c => c.key),
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        ...modulePremiums,
                    },
                });
            });
        });

        const plan = {
            plan_id:      effectivePlanId,
            uid:          planUid,
            base_plan_id: selectedPlan,
            plan:         selectedPlanObj.key,
            insurer:      'morgan_price',
            label: `Morgan Price ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                optional_covers: activeOptionalCovers.map(c => c.key),
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: '#0e54b1',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>Flexible Choices Asia</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/images/mp-international-01.svg`}
                        alt="Morgan Price International"
                        style={{ height: 20, filter: 'brightness(0) invert(1)', userSelect: 'none', WebkitUserDrag: 'none' }}
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                    />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={selectedPlan}
                            disabled={loading || !!editingPlan}
                            // Rates for every module come back in one fetch, so switching plans just shows different rows
                            onChange={(e) => setSelectedPlan(e.target.value)}
                            sx={selectSx}
                        >
                            {PLANS.map(plan => (
                                <MenuItem key={plan.id} value={plan.id} sx={menuItemSx}>{plan.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Optional Cover */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ fontSize: 15 }}>Optional Cover</Typography>
                    {OPTIONAL_COVERS.map(cover => (
                        <FormControlLabel
                            key={cover.key}
                            sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedOptionalCovers.has(cover.key)}
                                    onChange={() => toggleOptionalCover(cover.key)}
                                    disabled={loading || !!editingPlan}
                                />
                            }
                            label={cover.label}
                        />
                    ))}
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={areaOfCoverage}
                                disabled={loading}
                                onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }}
                                sx={selectSx}
                            >
                                {areaOptions.map(area => (
                                    <MenuItem key={area} value={area} sx={menuItemSx}>{getAreaLabel(area)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area of Coverage</Typography>
                        <Typography sx={{ fontSize: 12 }}>{areaOfCoverage}</Typography>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }}
                                sx={selectSx}
                            >
                                {PAYMENT_FREQUENCIES.map(f => (
                                    <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Excess per Quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Excess applicable to In-Patient treatment</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...excessOptions]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                    isZeroDeductibleUniform={false}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Rates Display */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}{quoteDeductibles[quote.name] === LEAVE_BLANK ? '' : ' Excess'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 250 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* One row per active module / optional cover */}
                                            {allActiveModules.map((module, moduleIdx) => (
                                                <TableRow key={module.key} sx={{ backgroundColor: moduleIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{module.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getRawRate(idx, module.key, quote.uid), currency)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(sumClients(i => getRawRate(i, module.key, quote.uid)), currency)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {/* Total row — same as MorganPrice_Rates' "<plan> Total Premium" */}
                                            {allActiveModules.length > 1 && (
                                                <TableRow sx={{ backgroundColor: '#bdbdbd' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>{buildPlanLabel()} Total Premium</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                            {formatRate(getRate(idx, quote.uid), currency)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        {formatRate(sumClients(i => getRate(i, quote.uid)), currency)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="morgan_price" />

                <RatesVersionSelect
                    insurer="morgan_price"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
